import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input, OnDestroy,
  Output,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {Offer} from '../../../../model/offer/offer';
import {OfferService} from '../../../../services/offer/offer.service';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {OfferFormBase} from '../create-offer-elements/offer-form-base/offer-form-base.component';
import {combineLatest, map, Observable, take} from 'rxjs';
import {Store} from '@ngrx/store';
import {Route} from '../../../../enum/route';
import {selectDetailPageProposalId} from '../../../../store/proposal/selectors/proposal.selectors';
import {ProposalAppState} from '../../../../store/proposal/reducers/proposal.reducer';
import {DetailsPageTab} from '../../../../enum/details-page-tabs';
import {ProposalDetailPageActions} from '../../../../store/proposal/actions/proposal.actions';
import {Roles} from '../../../../enum/roles';
import {selectUser} from '../../../../store/user/selectors/user.selectors';
import {UserAppState} from '../../../../store/user/reducers/user.reducer';
import {User} from '../../../../model/user';
import {SpinnerDialogComponent} from '../../../dialogs/spinner-dialog/spinner-dialog.component';
import {OfferFileUploadComponent} from '../create-offer-elements/offer-file-upload/offer-file-upload.component';
import {
  IndividualInformationFormComponent
} from '../create-offer-elements/individual-information-form/individual-information-form.component';

import {NgScrollbarModule} from 'ngx-scrollbar';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {ProposalUser} from '../../../../model/proposal/proposalUser';
import {OfferTransferService} from '../../../../services/offer/offer-transfer.service';
import {FileUploadService} from '../../../../services/file-upload-service/file-upload.service';

@Component({
    selector: 'app-create-offer-container',
    templateUrl: './create-offer-container.component.html',
    styleUrls: ['../../../../../styles/standard-elements.scss', '../../../../../styles/create-something.scss', './create-offer-container.component.scss', '../../../../../styles/form-input.scss'],
    standalone: false
})
export class CreateOfferContainerComponent implements AfterViewInit, OnDestroy {


  idSections = [];

  maxScrolled = 0;
  // Gap to select the section before it is reached
  scrollGap: number = 0.75;
  scrollPosition = [];
  scrolled = 0;

  @Output() uploadInProgressEmitter = new EventEmitter<boolean>();


  @ViewChildren(OfferFormBase) formComponents: QueryList<OfferFormBase>;
  @ViewChildren(NgScrollbarModule) perfectScrollbar: NgScrollbarModule;
  @ViewChild(IndividualInformationFormComponent) individualInformationForm: IndividualInformationFormComponent;
  @ViewChild(OfferFileUploadComponent) offerFileUploadComponent: OfferFileUploadComponent;
  offer = new Offer();
  allFormsValid$: Observable<boolean>;
  validChildForm$: Observable<boolean>[] = [];
  user$ = this.userStore.select(selectUser);
  user: User;
  Roles = Roles;
  insuranceContractViewingId$ = this.proposalStore.select(selectDetailPageProposalId);
  @Input() maxCoverage = 100;
  @Input() basedOnOffer!: Offer | null;
  uploadInProgress: boolean = false;

  //needed to forward the offerId to preview if the offer is based on another one
  @Input() basedOnOfferId: string | null = null;

  titles = ['coverage',
    'premium',
    'clauses',
    'creator'
  ];

  constructor(public offerService: OfferService,
              public dialog: MatDialog,
              readonly translate: TranslateService,
              private readonly router: Router,
              private readonly changeDetector: ChangeDetectorRef,
              private readonly proposalStore: Store<ProposalAppState>,
              private readonly userStore: Store<UserAppState>,
              private readonly offerTransferService: OfferTransferService,
              public fileUploadService: FileUploadService
  ) {
  }

  ngOnInit() {
    for (let i = 0; i < this.titles.length; i++) {
      this.idSections.push('question-block' + i);
    }

  }

  prefillForm(offer: Offer): void {
    this.formComponents.forEach(formComponent => {
      formComponent.prefill(offer);
    });

    if (this.individualInformationForm) {
      this.individualInformationForm.prefill(offer);
    }

    if (this.offerFileUploadComponent) {
      this.offerFileUploadComponent.prefill(offer);
    }
  }


  scroll(id) {
    document.getElementById(this.idSections[id]).scrollIntoView();
    this.scrolled = id;
  }

  ngAfterViewInit(): void {
    // Calculate the positions of each section relative to the top of the scrolling container
    this.titles.forEach((_, index) => {
      const sectionElement = document.getElementById(this.idSections[index]);
      if (sectionElement) {
        this.scrollPosition.push(sectionElement.offsetTop * this.scrollGap);
      } else {
        this.scrollPosition.push(0);
      }
    });
    // Add last fake position, so that last section is triggered
    this.scrollPosition.push(10000);

    const obs: Observable<boolean>[] = [];
    if (this.individualInformationForm) {
      this.offerFileUploadComponent = this.individualInformationForm.offerFileUpload;
    }
    for (let formbase of this.formComponents) {
      obs.push(formbase.validStatus());
      this.validChildForm$.push(formbase.validStatus());
    }
    this.allFormsValid$ = combineLatest(obs)
      .pipe(
        map((array: boolean[]) => array.every(value => value)),
      );

    //prefilling the form if there is an offer in the transfer service before the "new based on" because if the service is full means it's coming from preview and it has already changed
    if(this.offerTransferService.offer){
      this.prefillForm(this.offerTransferService.offer);
    } else if (this.basedOnOffer) {
      this.prefillForm(this.basedOnOffer);
    }

    //clearing the service, so if the user comes back to this page, the form is not prefilled again
    this.offerTransferService.clear();

    this.changeDetector.detectChanges();
  }

  ngOnDestroy(): void {
    this.fileUploadService.clearAll();
  }

  navigateToPreview(offerId?: string): void {
    const offer = new Offer();
    for (let form of this.formComponents) {
      form.updateOffer(offer);
    }
    this.offerFileUploadComponent.updateOffer(offer);

    this.user$.pipe(take(1)).subscribe(user => {
      offer.creator = new ProposalUser({ id: user.id });
      this.insuranceContractViewingId$.pipe(take(1)).subscribe(id => {
        offer.proposalId = id;

        if(offerId){
          this.offerTransferService.basedOnOfferId = offerId;
        }

        this.offerTransferService.offer = offer;

        this.router.navigate(['/' + Route.OFFER_PREVIEW]).then();
      });
    });
  }

  onScrollY(event: any) {
    const scrollTop = event.target.scrollTop;
    for (let i = 0; i < this.scrollPosition.length - 1; i++) {
      if (scrollTop >= this.scrollPosition[i] && scrollTop < this.scrollPosition[i + 1]) {
        this.scrolled = i;
        this.changeDetector.detectChanges();
        break;
      }
    }
  }

  receiveUploadStatus(data: boolean) {
    this.disableSaveOffer(data);
  }

  private disableSaveOffer(data: boolean) {
    this.uploadInProgress = data;
    this.uploadInProgressEmitter.emit(data);
  }

  // needed, don't delete
  private refillOffer(dialogRef: MatDialogRef<SpinnerDialogComponent, any>, newOffer: Offer) {
    this.offerService.refillOffer(newOffer).subscribe(() => {
      this.proposalStore.dispatch(ProposalDetailPageActions.load({proposalId: this.offer.proposalId}));
      this.router.navigate(['/' + Route.PROPOSAL_DETAILS, newOffer.proposalId], {queryParams: {tab: DetailsPageTab.OFFERS}}).then(() => {
        dialogRef.close();
      });
    });
  }
}
